/**
 * File: people.ts
 * Path: packages/config/src/leads/people.ts
 * Description: The extra people and branches on a lead or client. The main
 * contact stays in the record's own contactName/phone/email; these lists add
 * everyone else (a manager, the owner's partner) and the business's branches.
 * A branch that runs on its own can be split off into its own lead, linked
 * both ways, so it can be sold to separately.
 */

export const MAX_RECORD_CONTACTS = 30;
export const MAX_RECORD_BRANCHES = 30;

export type RecordKind = "leads" | "clients";

export interface RecordLink {
  kind: RecordKind;
  id: string;
  name: string;
}

export interface RecordContact {
  id: string;
  name: string;
  /** e.g. "Owner", "Office manager". */
  role: string;
  phone: string;
  email: string;
  /** The branch they work at; null for head office or the whole business. */
  branchId: string | null;
}

export interface RecordBranch {
  id: string;
  name: string;
  address: string;
  phone: string;
  /** Set once the branch was split off into its own lead. */
  linked: RecordLink | null;
}

const str = (value: unknown, max: number): string =>
  typeof value === "string" ? value.trim().slice(0, max) : "";

const toLink = (value: unknown): RecordLink | null => {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const kind = v.kind === "leads" || v.kind === "clients" ? v.kind : null;
  const id = str(v.id, 200);
  return kind && id ? { kind, id, name: str(v.name, 200) } : null;
};

export const toRecordContacts = (value: unknown): RecordContact[] =>
  (Array.isArray(value) ? value : [])
    .map((raw): RecordContact | null => {
      if (!raw || typeof raw !== "object") return null;
      const c = raw as Record<string, unknown>;
      const id = str(c.id, 64);
      const name = str(c.name, 200);
      if (!id || !name) return null;
      const branchId = str(c.branchId, 64);
      return {
        id,
        name,
        role: str(c.role, 100),
        phone: str(c.phone, 40),
        email: str(c.email, 200),
        branchId: branchId || null,
      };
    })
    .filter((c): c is RecordContact => c !== null)
    .slice(0, MAX_RECORD_CONTACTS);

export const toRecordBranches = (value: unknown): RecordBranch[] =>
  (Array.isArray(value) ? value : [])
    .map((raw): RecordBranch | null => {
      if (!raw || typeof raw !== "object") return null;
      const b = raw as Record<string, unknown>;
      const id = str(b.id, 64);
      const name = str(b.name, 200);
      if (!id || !name) return null;
      return {
        id,
        name,
        address: str(b.address, 300),
        phone: str(b.phone, 40),
        linked: toLink(b.linked),
      };
    })
    .filter((b): b is RecordBranch => b !== null)
    .slice(0, MAX_RECORD_BRANCHES);

/** The group a split-off branch belongs to: the record it was split from. */
export const toRecordGroup = (value: unknown): RecordLink | null => toLink(value);

/** The lead a branch becomes when it is split off: its own details, its own people. */
export const leadFromBranch = (
  parent: { name: string; niche: string; area?: string; website: string },
  branch: RecordBranch,
  contacts: readonly RecordContact[],
) => {
  const own = contacts.filter((c) => c.branchId === branch.id);
  const [main, ...rest] = own;
  return {
    name: `${parent.name} (${branch.name})`.slice(0, 200),
    contactName: main?.name ?? "",
    phone: branch.phone || main?.phone || "",
    email: main?.email ?? "",
    website: parent.website,
    address: branch.address,
    area: parent.area ?? "",
    niche: parent.niche,
    value: null,
    nextFollowUp: null,
    doNotContact: false,
    marketingConsent: false,
    contacts: rest.map((c) => ({ ...c, branchId: null })),
    branches: [] as RecordBranch[],
  };
};
